import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const getToken = async (user: any): Promise<string> => {
  const local = localStorage.getItem('accessToken');
  if (local) return local;
  if (user?.getIdToken) return user.getIdToken();
  return '';
};

interface StepResult {
  stepId: string;
  order: number;
  action: string;
  status: 'completed' | 'skipped' | 'escalated' | 'failed';
  output?: string;
  duration: number;
  guardianEscalated: boolean;
}

interface Execution {
  id: string;
  status: string;
  currentStep: number;
  totalSteps: number;
  stepResults: string;
  alignmentScoreAtExec: number;
  autonomyModeAtExec: string;
  userRating?: number;
  userFeedback?: string;
  duration?: number;
  guardianEscalations: number;
  startedAt: string;
  completedAt?: string;
}

interface Props {
  replicaId: string;
  skillId: string;
  skillName: string;
  onClose: () => void;
}

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const ACTION_ICONS: Record<string, string> = {
  send_email: '📧',
  create_doc: '📄',
  search: '🔍',
  summarize: '📝',
  ask_human: '🙋',
  wait: '⏳',
  decide: '🤔',
  notify: '🔔',
  api_call: '🔌',
  transform_data: '⚙️',
};

const STATUS_COLORS: Record<string, { bg: string; text: string; icon: string }> = {
  completed: { bg: '#22c55e20', text: '#22c55e', icon: '✅' },
  skipped: { bg: '#64748b20', text: '#64748b', icon: '⏭️' },
  escalated: { bg: '#f59e0b20', text: '#f59e0b', icon: '🛡️' },
  failed: { bg: '#ef444420', text: '#ef4444', icon: '❌' },
};

export const SkillReplay: React.FC<Props> = ({ replicaId, skillId, skillName, onClose }) => {
  const { user } = useAuth();
  const [executions, setExecutions] = useState<Execution[]>([]);
  const [selectedExec, setSelectedExec] = useState<Execution | null>(null);
  const [steps, setSteps] = useState<StepResult[]>([]);
  const [playIndex, setPlayIndex] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');

  // ── Load execution history ──────────────────────────────────
  const loadHistory = useCallback(async () => {
    try {
      const authToken = await getToken(user);
      const res = await fetch(`${API_BASE}/api/v1/skills/${replicaId}/skills/${skillId}/history?limit=20`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await res.json();
      if (data.success) {
        setExecutions(data.history || []);
        if (data.history?.length > 0) {
          selectExecution(data.history[0]);
        }
      }
    } catch (e) {
      console.error('Failed to load history:', e);
    } finally {
      setLoading(false);
    }
  }, [replicaId, skillId, user]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const selectExecution = (exec: Execution) => {
    setSelectedExec(exec);
    try {
      const parsed = JSON.parse(exec.stepResults || '[]');
      setSteps(parsed);
    } catch {
      setSteps([]);
    }
    setPlayIndex(-1);
    setPlaying(false);
  };

  // ── Playback ────────────────────────────────────────────────
  useEffect(() => {
    if (!playing || playIndex >= steps.length - 1) {
      if (playing && playIndex >= steps.length - 1) setPlaying(false);
      return;
    }
    const timer = setTimeout(() => setPlayIndex(i => i + 1), 800);
    return () => clearTimeout(timer);
  }, [playing, playIndex, steps.length]);

  const handlePlay = () => {
    if (playIndex >= steps.length - 1) setPlayIndex(-1);
    setPlaying(true);
    if (playIndex < 0) setPlayIndex(0);
  };

  // ── Submit Feedback ─────────────────────────────────────────
  const handleFeedback = async () => {
    if (!selectedExec || rating === 0) return;
    try {
      const authToken = await getToken(user);
      await fetch(`${API_BASE}/api/v1/skills/${replicaId}/executions/${selectedExec.id}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ rating, feedback: feedback || undefined }),
      });
      loadHistory();
    } catch (e) {
      console.error('Feedback failed:', e);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 border border-slate-600/50 rounded-2xl w-[700px] max-h-[85vh] overflow-hidden shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700/50">
          <div className="flex items-center gap-3">
            <span className="text-xl">🔄</span>
            <div>
              <h3 className="text-base font-bold text-white">Skill Replay — {skillName}</h3>
              <p className="text-xs text-slate-400">{executions.length} executions recorded</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-sm">✕</button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Execution List */}
          <div className="w-52 border-r border-slate-700/50 overflow-y-auto">
            {executions.map(exec => (
              <button
                key={exec.id}
                onClick={() => selectExecution(exec)}
                className={`w-full text-left px-4 py-3 border-b border-slate-700/20 transition-colors ${
                  selectedExec?.id === exec.id ? 'bg-indigo-600/20' : 'hover:bg-slate-700/20'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className={`w-2 h-2 rounded-full ${
                    exec.status === 'completed' ? 'bg-green-400' :
                    exec.status === 'failed' ? 'bg-red-400' :
                    exec.status === 'cancelled' ? 'bg-slate-400' : 'bg-yellow-400'
                  }`} />
                  <span className="text-xs font-medium text-white capitalize">{exec.status}</span>
                </div>
                <p className="text-xs text-slate-400">
                  {new Date(exec.startedAt).toLocaleDateString()} {new Date(exec.startedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
                {exec.duration && (
                  <p className="text-xs text-slate-500">{exec.duration.toFixed(1)}s</p>
                )}
                {exec.userRating && (
                  <div className="flex gap-0.5 mt-1">
                    {[1, 2, 3, 4, 5].map(s => (
                      <span key={s} className={`text-xs ${s <= exec.userRating! ? 'text-yellow-400' : 'text-slate-600'}`}>★</span>
                    ))}
                  </div>
                )}
              </button>
            ))}
            {executions.length === 0 && (
              <div className="p-6 text-center">
                <span className="text-2xl">📭</span>
                <p className="text-xs text-slate-400 mt-2">No executions yet</p>
              </div>
            )}
          </div>

          {/* Replay View */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {selectedExec ? (
              <>
                {/* Execution Info Bar */}
                <div className="flex items-center gap-4 px-5 py-3 border-b border-slate-700/30 bg-slate-800/50">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400">Alignment:</span>
                    <span className="text-xs font-bold text-indigo-400">{Math.round(selectedExec.alignmentScoreAtExec * 100)}%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400">Mode:</span>
                    <span className="text-xs font-medium text-white capitalize">{selectedExec.autonomyModeAtExec}</span>
                  </div>
                  {selectedExec.guardianEscalations > 0 && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400">
                      🛡️ {selectedExec.guardianEscalations} escalation{selectedExec.guardianEscalations > 1 ? 's' : ''}
                    </span>
                  )}
                  <div className="flex-1" />
                  {/* Playback controls */}
                  <button onClick={handlePlay} disabled={steps.length === 0}
                    className="px-3 py-1 bg-indigo-600/20 border border-indigo-500/30 text-indigo-300 text-xs font-medium rounded-lg hover:bg-indigo-600/30 transition-colors disabled:opacity-40">
                    {playing ? '⏸ Pause' : '▶ Play'}
                  </button>
                  <button onClick={() => { setPlayIndex(-1); setPlaying(false); }}
                    className="px-3 py-1 bg-slate-700/30 text-slate-400 text-xs rounded-lg hover:text-white transition-colors">
                    ⏮ Reset
                  </button>
                </div>

                {/* Step Timeline */}
                <div className="flex-1 overflow-y-auto p-5">
                  <div className="relative">
                    {/* Vertical line */}
                    <div className="absolute left-5 top-0 bottom-0 w-px bg-slate-700/50" />

                    {steps.map((step, i) => {
                      const isActive = i === playIndex;
                      const isPast = i < playIndex || playIndex === -1;
                      const statusStyle = STATUS_COLORS[step.status] || STATUS_COLORS.completed;
                      const actionIcon = ACTION_ICONS[step.action] || '⚙️';

                      return (
                        <div key={i}
                          className={`relative flex items-start gap-4 mb-4 pl-10 transition-all duration-300 ${
                            isActive ? 'scale-[1.02]' : isPast ? 'opacity-80' : 'opacity-40'
                          }`}
                          onClick={() => { setPlayIndex(i); setPlaying(false); }}
                        >
                          {/* Node */}
                          <div className={`absolute left-3 w-5 h-5 rounded-full border-2 flex items-center justify-center text-xs transition-all ${
                            isActive ? 'border-indigo-400 bg-indigo-500/20 ring-2 ring-indigo-500/30' :
                            isPast ? `border-slate-600` : 'border-slate-700 bg-slate-800'
                          }`}
                            style={isPast ? { borderColor: statusStyle.text, backgroundColor: statusStyle.bg } : {}}
                          >
                            {isPast && <span className="text-[8px]">{statusStyle.icon}</span>}
                          </div>

                          {/* Content */}
                          <div className={`flex-1 rounded-xl p-3 border cursor-pointer transition-colors ${
                            isActive ? 'bg-indigo-500/10 border-indigo-500/30' : 'bg-slate-700/10 border-slate-700/30 hover:bg-slate-700/20'
                          }`}>
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-2">
                                <span className="text-sm">{actionIcon}</span>
                                <span className="text-xs font-bold text-white">{step.action.replace(/_/g, ' ')}</span>
                                {step.guardianEscalated && <span className="text-xs text-yellow-400">🛡️</span>}
                              </div>
                              <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: statusStyle.bg, color: statusStyle.text }}>
                                {step.status}
                              </span>
                            </div>
                            {step.output && (
                              <p className="text-xs text-slate-400 mt-1 truncate">{step.output}</p>
                            )}
                            <span className="text-xs text-slate-500">{step.duration}ms</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Feedback */}
                {selectedExec.status === 'completed' && !selectedExec.userRating && (
                  <div className="px-5 py-3 border-t border-slate-700/30 bg-slate-800/50">
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-400">Rate:</span>
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map(s => (
                          <button key={s} onClick={() => setRating(s)}
                            className={`text-lg transition-colors ${s <= rating ? 'text-yellow-400' : 'text-slate-600 hover:text-slate-400'}`}>
                            ★
                          </button>
                        ))}
                      </div>
                      <input value={feedback} onChange={e => setFeedback(e.target.value)}
                        placeholder="Optional feedback"
                        className="flex-1 bg-slate-700/30 border border-slate-600/30 rounded-lg px-3 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none" />
                      <button onClick={handleFeedback} disabled={rating === 0}
                        className="px-3 py-1.5 bg-green-600/20 border border-green-500/30 text-green-300 text-xs rounded-lg disabled:opacity-40 hover:bg-green-600/30 transition-colors">
                        Submit
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <p className="text-sm text-slate-400">Select an execution to replay</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SkillReplay;
